import dayjs from "dayjs";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import MESSAGES from "../../../../common/data/constants/messages";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardBody, CardFooterRight, CardHeader, CardLabel, CardTitle } from "../../../../components/bootstrap/Card";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../components/bootstrap/forms/Input";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../components/bootstrap/Modal";
import Spinner from "../../../../components/bootstrap/Spinner";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import workOrderEvidenceService from "../../../../services/work-order-evidence-service";

interface Props {
    isModalOpened: boolean;
    onModalClose: () => void;
    onAfterSubmit: () => void;
    workOrderId: string;
}

type EvidenceFormValues = {
    beforeShotFile: string | null;
    beforeShotLocation: string | null;
    beforeShotDate: string;
    afterShotFile: string | null;
    afterShotLocation: string | null;
    afterShotDate: string;
}

export const WorkOrderEvidenceModal = ({ isModalOpened, onModalClose, onAfterSubmit, workOrderId }: Props) => {

    const [beforePreview, setBeforePreview] = useState<string>('');
    const [afterPreview, setAfterPreview] = useState<string>('');

    const [beforeFile, setBeforeFile] = useState<File | null>(null);
    const [afterFile, setAfterFile] = useState<File | null>(null);

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const formik = useFormik({
        initialValues: {
            beforeShotFile: '',
            beforeShotLocation: '',
            beforeShotDate: '',
            afterShotFile: '',
            afterShotLocation: '',
            afterShotDate: ''
        },

        validate: (values) => {
            const errors: {
                beforeShotFile?: string;
                beforeShotLocation?: string;
                beforeShotDate?: string;
                afterShotFile?: string;
                afterShotLocation?: string;
                afterShotDate?: string;
            } = {};

            if (!values.beforeShotLocation) errors.beforeShotLocation = MESSAGES.VALIDATION.REQUIRED_FIELD;
            if (!values.beforeShotDate) errors.beforeShotDate = MESSAGES.VALIDATION.REQUIRED_FIELD;
            if (!values.afterShotLocation) errors.afterShotLocation = MESSAGES.VALIDATION.REQUIRED_FIELD;
            if (!values.afterShotDate) errors.afterShotDate = MESSAGES.VALIDATION.REQUIRED_FIELD;

            return errors;
        },

        onSubmit: (values, { resetForm }) => {
            onFormSubmit(values);
            resetForm();
        },
    });

    useEffect(() => {
        if (isModalOpened) {
            resetForm();
        }
    }, [isModalOpened]);

    const handleBeforeFileChange = (event: any) => {
        const file = event.target.files ? event.target.files[0] : null;

        if (file) {
            const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                showWarning('Formato inválido. Formatos aceitos: JPG, PNG e WebP.');
                return;
            }

            setBeforeFile(file);

            const reader = new FileReader();
            reader.onload = (e) => {
                setBeforePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAfterFileChange = (event: any) => {
        const file = event.target.files ? event.target.files[0] : null;

        if (file) {
            const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                showWarning('Formato inválido. Formatos aceitos: JPG, PNG e WebP.');
                return;
            }

            setAfterFile(file);

            const reader = new FileReader();
            reader.onload = (e) => {
                setAfterPreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const onClose = () => {
        onModalClose();
    };

    const onFormSubmit = async (values: EvidenceFormValues) => {
        if (!beforeFile || !afterFile) {
            showWarning('Evidências de antes e/ou depois não encontradas');
            return;
        }

        if (!workOrderId) {
            showWarning('Código da order de serviço não encontrado');
            return;
        }

        setIsSubmitting(true);

        const formData = new FormData();

        formData.append('BeforeShotFile', beforeFile);
        formData.append('AfterShotFile', afterFile);

        formData.append('BeforeShotLocation', values.beforeShotLocation || '');
        formData.append('AfterShotLocation', values.afterShotLocation || '');

        if (values.beforeShotDate) {
            formData.append('BeforeShotDate', dayjs(values.beforeShotDate).toISOString());
        }
        if (values.afterShotDate) {
            formData.append('AfterShotDate', dayjs(values.afterShotDate).toISOString());
        }

        if (workOrderId) {
            formData.append('WorkOrderId', workOrderId);
        }

        const response = await workOrderEvidenceService.createWorkOrderEvidence(formData);

        setIsSubmitting(false);

        if (response.error) {
            showWarning(MESSAGES.ACTION.SAVE_ERROR);
            return;
        }

        showSuccess(MESSAGES.ACTION.SAVED);
        resetForm();
        onAfterSubmit();
    };

    const resetForm = () => {
        formik.resetForm({
            values: {
                beforeShotFile: '',
                beforeShotLocation: '',
                beforeShotDate: '',
                afterShotFile: '',
                afterShotLocation: '',
                afterShotDate: ''
            },
        });

        setBeforeFile(null);
        setBeforePreview('');
        setAfterFile(null);
        setAfterPreview('');
    };

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'xl'} isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id='evidence-modal'>Evidências</ModalTitle>
            </ModalHeader>
            <ModalBody>

                <Card style={{ minHeight: 300 }}>
                    <CardBody>
                        <Card style={{ minHeight: 100 }}>
                            <CardHeader>
                                <CardLabel>
                                    <CardTitle tag="div" className="h6">
                                        Evidência Antes
                                    </CardTitle>
                                </CardLabel>
                            </CardHeader>
                            <CardBody>
                                <div className="row g-4">

                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <FormGroup id="beforeShot" isFloating>
                                            <Input
                                                type='file'
                                                ariaLabel='Envie evidências do "Antes"'
                                                onChange={handleBeforeFileChange}
                                                accept="image/jpeg, image/png, image/webp"
                                            />
                                        </FormGroup>
                                    </div>

                                    <div className="col-lg-6 col-md-12 col-sm-12">
                                        <FormGroup id="beforeShotLocation" label="Localização" isFloating>
                                            <Input
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.beforeShotLocation}
                                                isValid={formik.isValid}
                                                isTouched={formik.touched.beforeShotLocation}
                                                invalidFeedback={formik.errors.beforeShotLocation}
                                                validFeedback=""
                                            />
                                        </FormGroup>
                                    </div>

                                    <div className="col-lg-6 col-md-12 col-sm-12">
                                        <FormGroup id="beforeShotDate" label="Localização" isFloating>
                                            <Input
                                                type="date"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.beforeShotDate}
                                                isValid={formik.isValid}
                                                isTouched={formik.touched.beforeShotDate}
                                                invalidFeedback={formik.errors.beforeShotDate}
                                                validFeedback=""
                                            />
                                        </FormGroup>
                                    </div>

                                </div>
                            </CardBody>
                        </Card>
                    </CardBody>
                </Card>

                <Card style={{ minHeight: 300 }}>
                    <CardBody>
                        <Card style={{ minHeight: 100 }}>
                            <CardHeader>
                                <CardLabel>
                                    <CardTitle tag="div" className="h6">
                                        Evidência Depois
                                    </CardTitle>
                                </CardLabel>
                            </CardHeader>
                            <CardBody>
                                <div className="row g-4">

                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <FormGroup id="afterShot" isFloating>
                                            <Input
                                                type='file'
                                                ariaLabel='Envie evidências do "Depois"'
                                                onChange={handleAfterFileChange}
                                                accept="image/jpeg, image/png, image/webp"
                                            />
                                        </FormGroup>
                                    </div>

                                    <div className="col-lg-6 col-md-12 col-sm-12">
                                        <FormGroup id="afterShotLocation" label="Localização" isFloating>
                                            <Input
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.afterShotLocation}
                                                isValid={formik.isValid}
                                                isTouched={formik.touched.afterShotLocation}
                                                invalidFeedback={formik.errors.afterShotLocation}
                                                validFeedback=""
                                            />
                                        </FormGroup>
                                    </div>

                                    <div className="col-lg-6 col-md-12 col-sm-12">
                                        <FormGroup id="afterShotDate" label="Localização" isFloating>
                                            <Input
                                                type="date"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.afterShotDate}
                                                isValid={formik.isValid}
                                                isTouched={formik.touched.afterShotDate}
                                                invalidFeedback={formik.errors.afterShotDate}
                                                validFeedback=""
                                            />
                                        </FormGroup>
                                    </div>

                                </div>
                            </CardBody>
                        </Card>
                    </CardBody>
                </Card>


            </ModalBody >
            <ModalFooter className='bg-transparent'>
                <CardFooterRight>
                    <Button color='success' className='w-100' onClick={formik.handleSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal >
    );

}